import React from 'react';

// Surfaces failures from the sub-card auto-image regeneration pipeline.
// Two flavours:
//   1. Hard halt — generation didn't run at all (e.g. manhole angle missing,
//      orientation undetermined). `haltReason` + `haltDetail` are set.
//   2. Per-output failures — some outputs succeeded, some didn't. The
//      successful ones are already created and selected; `failedOutputs`
//      lists the rest so the admin can be told exactly which file is needed.
export default function SubcardImageWarningModal({
  subcardName,
  component,
  measurement,
  layout,
  haltReason,
  haltDetail,
  failedOutputs,
  onClose,
}) {
  const hasHalt = !!haltReason;
  const hasFailures = Array.isArray(failedOutputs) && failedOutputs.length > 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Reference image not found</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <p className="confirm-text">
          One or more reference images couldn't be generated for this sub-card.
          Share the details below with the admin so they can add the missing
          assets — then click <strong>Apply</strong> again to retry.
        </p>

        <div style={{
          background: 'var(--bg-light)',
          border: '1px solid var(--border-muted)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)',
          marginTop: 'var(--space-sm)',
          fontSize: '0.85rem',
          lineHeight: 1.6,
        }}>
          <div><strong>Sub-card:</strong> {subcardName || '—'}</div>
          <div><strong>Component:</strong> {component || '—'}</div>
          <div><strong>Measurement:</strong> {measurement || '—'}</div>
          <div style={{ marginTop: 6 }}>
            <strong>Inputs:</strong>{' '}
            {Object.entries(layout || {})
              .filter(([_, v]) => v !== '' && v !== null && v !== undefined)
              .map(([k, v]) => `${k}=${v}`)
              .join(', ') || '(none)'}
          </div>

          {hasHalt && (
            <div style={{ marginTop: 10 }}>
              <strong>Reason:</strong> <code>{haltReason}</code>
              {haltDetail && <div style={{ marginTop: 4 }}>{haltDetail}</div>}
            </div>
          )}

          {hasFailures && (
            <div style={{ marginTop: 10 }}>
              <strong>Failed outputs:</strong>
              <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 18 }}>
                {failedOutputs.map((f, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    Output {f.output_index + 1} —{' '}
                    <code>{f.error_code}</code>:{' '}
                    {f.failed_filename && <code>{f.failed_filename}</code>}{' '}
                    {f.error_detail && <span style={{ color: 'var(--text-muted)' }}>({f.error_detail})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
}
